import ArrowLeftIcon from "@atlaskit/icon/core/arrow-left";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {token} from "@atlaskit/tokens";
import {useState} from "react";
import {twJoin} from "tailwind-merge";
import {BottomDrawer} from "~/components/BottomDrawer";
import {
    ConfigureColumnsEmbeddedFooter,
    ConfigureColumnsEmbeddedPanel,
    ConfigureColumnsEmbeddedScope,
} from "~/components/ottopilotDataViewer/configureColumnsComponent";
import {
    CalendarCustomizeViewPanelBody,
    CalendarCustomizeViewPanelFooter,
} from "~/components/ottopilotDataViewer/calendarCustomizeViewPanel";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {sortOttopilotViewsForDisplayOrder, ViewType} from "~/components/ottopilotDataViewer/view";
import {viewTabLabel, ViewPickerDrawerContent} from "~/components/ottopilotDataViewer/viewPicker";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";

type OverflowScreen = "menu" | "views" | "columns" | "customize";

function DrawerApplyCancelFooter({
    onCancel,
    onApply,
    applyDisabled = false,
}: {
    onCancel: () => void;
    onApply: () => void;
    applyDisabled?: boolean;
}) {
    return (
        <div className="py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
            <AnankeButton type="button" appearance="subtle" onClick={onCancel}>
                Cancel
            </AnankeButton>
            <AnankeButton type="button" appearance="primary" onClick={onApply} isDisabled={applyDisabled}>
                Apply
            </AnankeButton>
        </div>
    );
}

function OverflowMenuOptionRow({
    label,
    subtitle,
    onClick,
    disabled = false,
}: {
    label: string;
    subtitle?: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={twJoin(
                "w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-ds-space-150",
                "min-h-[40px] px-ds-space-150 py-ds-space-100 rounded-[4px] text-left",
                "bg-ds-background-input hover:bg-ds-background-neutral-hovered",
                "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
            aria-label={label}
        >
            <div className="min-w-0 text-left">
                <span className="block w-full text-left text-sm font-medium text-ds-text truncate">{label}</span>
                {subtitle !== undefined && subtitle.length > 0 ? (
                    <span className="block w-full text-left text-sm text-ds-text-subtle truncate">{subtitle}</span>
                ) : null}
            </div>
            <span className="shrink-0 grid place-items-center">
                <ChevronRightIcon label="" color={token("color.icon.subtle")} />
            </span>
        </button>
    );
}

function DrawerTitleWithBack({title, onBack}: {title: string; onBack: () => void}) {
    return (
        <div className="grid min-w-0 grid-cols-[max-content_minmax(0,1fr)] items-center gap-ds-space-100">
            <AnankeIconButton
                type="button"
                appearance="subtle"
                spacing="compact"
                icon={ArrowLeftIcon}
                label="Back"
                onClick={onBack}
            />
            <span
                className="truncate min-w-0 text-[1.25rem] leading-ds-space-300 font-ds-font-weight-bold"
                style={{color: token("color.text", "#172B4D"), fontFamily: "Inter"}}
            >
                {title}
            </span>
        </div>
    );
}

export function MobileDataViewerOverflowMenu<
    T extends {
        id: Uuid;
    },
>({
    controller,
    onViewIdApplied,
}: {
    controller: OttopilotDataViewerController<T>;
    /** Called after the user applies a view from the overflow drawer. */
    onViewIdApplied?: (viewId: Uuid) => void;
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [screen, setScreen] = useState<OverflowScreen>("menu");
    const [pendingViewId, setPendingViewId] = useState<Uuid | null>(controller.currentViewId);
    const [pendingCalendarMetricId, setPendingCalendarMetricId] = useState(controller.calendarDisplayMetricId);

    const currentView =
        controller.currentViewId === null
            ? null
            : controller.views.find((view) => view.id === controller.currentViewId);

    const orderedViews = sortOttopilotViewsForDisplayOrder(controller.views);
    const hasMultipleViews = orderedViews.length > 1;
    const isConfigureColumnsEnabled =
        currentView !== null &&
        currentView !== undefined &&
        (currentView.viewType === ViewType.table || currentView.viewType === ViewType.kanbanBoard);
    const isCalendarCustomizeEnabled =
        currentView?.viewType === ViewType.calendar && controller.calendarDisplayMetrics !== undefined;

    const currentViewSubtitle =
        currentView !== null && currentView !== undefined
            ? viewTabLabel(currentView.viewType).toLowerCase()
            : "";

    if (hasMultipleViews === false && isConfigureColumnsEnabled === false && isCalendarCustomizeEnabled === false) {
        return null;
    }

    const closeDrawer = () => {
        setDrawerOpen(false);
        setScreen("menu");
    };

    const drawerTitle =
        screen === "menu" ? (
            "More menu"
        ) : (
            <DrawerTitleWithBack
                title={
                    screen === "views" ? "View by" : screen === "columns" ? "Columns" : "Customize view"
                }
                onBack={() => {
                    setScreen("menu");
                }}
            />
        );

    const drawerFooter =
        screen === "views" ? (
            <DrawerApplyCancelFooter
                onCancel={() => {
                    setScreen("menu");
                }}
                onApply={() => {
                    if (pendingViewId !== null) {
                        controller.setExplicitViewId(pendingViewId);
                        onViewIdApplied?.(pendingViewId);
                    }
                    closeDrawer();
                }}
                applyDisabled={pendingViewId === null}
            />
        ) : screen === "columns" ? (
            <ConfigureColumnsEmbeddedFooter />
        ) : screen === "customize" ? (
            <CalendarCustomizeViewPanelFooter
                onCancel={() => {
                    setScreen("menu");
                }}
                onConfirm={() => {
                    controller.setCalendarDisplayMetricId(pendingCalendarMetricId);
                    closeDrawer();
                }}
            />
        ) : undefined;

    const drawerBody =
        screen === "menu" ? (
            <div className="grid gap-ds-space-100 w-full">
                {hasMultipleViews === true ? (
                    <OverflowMenuOptionRow
                        label="View by"
                        subtitle={currentViewSubtitle}
                        onClick={() => {
                            setPendingViewId(controller.currentViewId);
                            setScreen("views");
                        }}
                    />
                ) : null}
                {isCalendarCustomizeEnabled === true ? (
                    <OverflowMenuOptionRow
                        label="Customize"
                        onClick={() => {
                            setPendingCalendarMetricId(controller.calendarDisplayMetricId);
                            setScreen("customize");
                        }}
                    />
                ) : null}
                <OverflowMenuOptionRow
                    label="Columns"
                    disabled={isConfigureColumnsEnabled === false}
                    onClick={() => {
                        if (isConfigureColumnsEnabled === false) {
                            return;
                        }
                        setScreen("columns");
                    }}
                />
            </div>
        ) : screen === "views" ? (
            <ViewPickerDrawerContent
                controller={controller}
                selectedViewId={pendingViewId}
                onSelectedViewIdChange={setPendingViewId}
            />
        ) : screen === "customize" ? (
            <CalendarCustomizeViewPanelBody
                controller={controller}
                ephemeralMetricId={pendingCalendarMetricId}
                onEphemeralMetricIdChange={setPendingCalendarMetricId}
                layout="mobile"
            />
        ) : (
            <ConfigureColumnsEmbeddedPanel />
        );

    return (
        <>
            <AnankeIconButton
                type="button"
                appearance="default"
                icon={ShowMoreHorizontalIcon}
                label="More menu"
                aria-expanded={drawerOpen}
                isSelected={drawerOpen}
                onClick={() => {
                    setScreen("menu");
                    setDrawerOpen(true);
                }}
            />
            <ConfigureColumnsEmbeddedScope
                active={screen === "columns"}
                controller={controller}
                onEmbeddedClose={closeDrawer}
                onEmbeddedCancel={() => {
                    setScreen("menu");
                }}
            >
                <BottomDrawer
                    open={drawerOpen}
                    setOpen={(value) => {
                        const next = typeof value === "function" ? value(drawerOpen) : value;
                        if (next === false) {
                            closeDrawer();
                        } else {
                            setDrawerOpen(true);
                        }
                    }}
                    title={drawerTitle}
                    zIndex={100}
                    fitContentOnOpen={screen !== "columns"}
                    initialOpenHeight={screen === "columns" ? 480 : undefined}
                    headerBottomBorder={true}
                    footer={drawerFooter}
                >
                    {drawerBody}
                </BottomDrawer>
            </ConfigureColumnsEmbeddedScope>
        </>
    );
}
